import {
  put, takeEvery, all, select, takeLatest, call,
} from 'redux-saga/effects';
import { getUser } from 'circe/authentication/selectors';

import { delay } from 'redux-saga';
import { show as showNotification } from 'circe/notifications/actions';
import * as I18n from 'circe/I18n';

import { getCurrentAuthHeader } from 'domain/fuse/auth/utils';
import { getCurrentProfile } from 'domain/fuse/auth/selectors';
import { startLoading, doneLoading } from 'domain/layout/actions';
import { profileInitialized } from 'domain/fuse/auth/profiles/actions';
import { exception } from 'circe/logger';
import { operatorClient, authClient } from 'domain/HttpClients';
import driversSaga from 'domain/operations/drivers/saga';
import { getShipperWithProfiles } from 'domain/operations/shippers/utils';

import {
  initShippers,
  initShipments,
  fetchDriverDocs,
  driverDocsFetched,
  approveDriverDoc,
  driverDocApproved,
  rejectDriverDoc,
  driverDocRejected,
  assignReferenceId,
  assignedReferenceId,
} from 'domain/operations/actions';

import {
  refreshShippers,
  refreshDrivers,
  refreshShipments,
} from 'domain/operations/accountManagement/actions';

function* loadOperatorData() {
  // // Some day annotations would be nice
  const state = yield select();
  const { role } = getCurrentProfile(state);
  //

  yield put(refreshShippers());

  if (role !== 'OPERATOR' && role !== 'ACCOUNT_ADMIN') return;
  yield put(
    startLoading('OPS_DATA', {
      urls: ['/drivers', '/shippers'],
    }),
  );
  yield put(refreshDrivers());
  yield put(refreshShipments());
  yield put(doneLoading('OPS_DATA'));
}

function* loadShippers() {
  try {
    yield put(
      startLoading('OPS_DATA_SHIPPERS', {
        urls: ['/shippers'],
      }),
    );
    const state = yield select();
    const headers = getCurrentAuthHeader(state);
    let allShippers = [];

    let resp = yield operatorClient.Get(`/api/shippers?did=${SYSTEM_DOMAIN}`).send();

    if (!resp) {
      throw new Error('Shippers failed to load');
    }

    yield put(initShippers(resp.results));
    allShippers = allShippers.concat(resp.results);
    allShippers = allShippers.map((currentShipper) => {
      const shipper = {
        ...currentShipper,
      };
      return getShipperWithProfiles(shipper);
    });
    while (resp.nextPage && resp.nextPage.hasResults) {
      yield delay(750);
      resp = yield operatorClient
        .Get('/api/shippers')
        .withParams({ did: SYSTEM_DOMAIN, cursor: resp.nextPage.cursor })
        .send();
      allShippers = allShippers.concat(resp.results);
      if (allShippers.length >= 10) {
        allShippers = allShippers.map((currentShipper) => {
          const shipper = {
            ...currentShipper,
          };
          if (shipper.hasOwnProperty('isAccountAdmin')) {
            return shipper;
          }
          return getShipperWithProfiles(shipper);
        });
        yield put(initShippers(allShippers));
      }
    }
    yield put(initShippers(allShippers));
  } catch (e) {
    exception(e, { shippers: 'Loading Shippers' });
  }
  yield put(doneLoading('OPS_DATA_SHIPPERS'));
}

function* doFetchDriverDocs({ payload }) {
  const { id } = payload;

  const response = yield operatorClient.Get(`/api/profiles/${id}/files`).send();

  yield put(
    driverDocsFetched(
      response.data.map((item) => ({
        ...item,
        fileLocation: `${item.fileLocation}.jpg`,
        fileName: `${item.fileLocation.split('/').pop()}.jpg`,
      })),
    ),
  );
}

function* doApproveDriverDoc({ payload }) {
  const { profileId, id, values } = payload;

  const state = yield select();
  // TODO Use selectors
  const docs = state.app.operations.driverDocs.data;

  const doc = {
    ...docs[docs.findIndex((doc) => doc.fileLocation === id)],
    approvalStatus: 'APPROVED',
    ...values,
  };
  yield operatorClient.Post(`/api/filecabinet/${profileId}/approve`).send();
  yield put(driverDocApproved(doc));
}

function* doRejectDriverDoc({ payload }) {
  const { profileId, id, values } = payload;

  const state = yield select();
  // TODO SELECTORS
  const docs = state.app.operations.driverDocs.data;

  const doc = {
    ...docs[docs.findIndex((doc) => doc.fileLocation === id)],
    approvalStatus: 'REJECTED',
    ...values,
  };
  yield operatorClient
    .Post(`/api/filecabinet/${profileId}/reject`)
    .withData(doc)
    .send();
  yield put(driverDocRejected(doc));
}

function* doAssignReferenceId({ payload }) {
  const { values } = payload;

  const { id, referenceId } = values;

  try {
    const data = {
      profileId: id,
      refId: referenceId,
    };

    const state = yield select();

    const user = getUser(state);

    const headers = {
      Authorization: `Bearer ${user.tokens.idToken}`,
    };

    yield authClient
      .Patch(`/api/self/update-refId/${id}`)
      .withData(data)
      .withHeaders(headers)
      .send();

    yield put(
      showNotification(
        I18n.str('referenceIdAssignedSuccessfully', 'Vendor code assigned successfully.'),
        'success',
      ),
    );
    yield put(assignedReferenceId());
  } catch (e) {
    yield put(
      showNotification(
        I18n.str('unableToAssignReferenceId', 'Unable to assign vendor code.'),
        'error',
      ),
    );
    yield put(assignedReferenceId());
  }
}

function* watcher(context) {
  yield all([
    takeEvery(profileInitialized().type, loadOperatorData),
    // takeEvery(refreshShippers().type, loadShippers),
    takeLatest(fetchDriverDocs().type, doFetchDriverDocs),
    takeEvery(approveDriverDoc().type, doApproveDriverDoc),
    takeEvery(rejectDriverDoc().type, doRejectDriverDoc),
    takeEvery(assignReferenceId().type, doAssignReferenceId),
    driversSaga(context),
  ]);
}

export default watcher;
